import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface QualityRow {
  readonly qualityId: string;
  readonly qualityCode: string;
  readonly datasetId: string;
  readonly dimension: string;
  readonly threshold: string | null;
  readonly score: string | null;
  readonly resultStatus: string;
  readonly severity: string;
  readonly recordsChecked: string;
  readonly recordsPassed: string;
}

const DIMENSIONS = ['COMPLETENESS', 'ACCURACY', 'CONSISTENCY', 'UNIQUENESS', 'INTEGRITY', 'TIMELINESS', 'VALIDITY', 'COMPLIANCE'];
const SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const RESULTS = ['PASS', 'WARN', 'FAIL'];
const PAGE = 25;

/**
 * M41 — Data Quality Dashboard (§26 / §9). The quality-framework surface over
 * the eight quality dimensions (T538). Define a quality rule for a dataset,
 * evaluate it (records checked / passed → score → PASS / WARN / FAIL, BR-04),
 * and filter the evaluation history. Viewing needs QUALITY_VIEW, defining needs
 * QUALITY_MANAGE, running an evaluation needs QUALITY_RUN.
 */
export function DataQualityDashboard(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<QualityRow[]>([]);
  const [total, setTotal] = useState(0);
  const [datasetFilter, setDatasetFilter] = useState('');
  const [dimensionFilter, setDimensionFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [datasetId, setDatasetId] = useState('');
  const [dimension, setDimension] = useState('COMPLETENESS');
  const [threshold, setThreshold] = useState('95');
  const [severity, setSeverity] = useState('MEDIUM');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [runChecked, setRunChecked] = useState<Record<string, string>>({});
  const [runPassed, setRunPassed] = useState<Record<string, string>>({});

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editThreshold, setEditThreshold] = useState('');
  const [editSeverity, setEditSeverity] = useState('MEDIUM');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (datasetFilter) params.set('datasetId', datasetFilter);
    if (dimensionFilter) params.set('dimension', dimensionFilter);
    if (resultFilter) params.set('resultStatus', resultFilter);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: QualityRow[]; total: number }>(`/v1/data-lake/data-quality?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, datasetFilter, dimensionFilter, resultFilter, page, reload]);

  const define = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!datasetId.trim()) { setActionError(t('m41.common.datasetIdRequired')); return; }
    try {
      await apiRequest('/v1/data-lake/data-quality', {
        method: 'POST',
        body: { datasetId: datasetId.trim(), dimension, threshold: Number(threshold), severity },
        token,
      });
      setNotice(t('m41.quality.ruleDefined'));
      setDatasetId('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.quality.notAuthDefine'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const run = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    const checked = Number(runChecked[id] ?? '');
    const passed = Number(runPassed[id] ?? '');
    if (!Number.isFinite(checked) || checked <= 0) { setActionError(t('m41.quality.checkedPositive')); return; }
    if (!Number.isFinite(passed) || passed < 0) { setActionError(t('m41.quality.passedNonNeg')); return; }
    try {
      await apiRequest(`/v1/data-lake/data-quality/${id}/run`, {
        method: 'POST',
        body: { recordsChecked: checked, recordsPassed: passed },
        token,
      });
      setNotice(t('m41.quality.evalComplete'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.quality.notAuthRun'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const startEdit = (q: QualityRow): void => {
    setEditingId(q.qualityId);
    setEditThreshold(q.threshold == null ? '' : String(q.threshold));
    setEditSeverity(q.severity);
    setActionError(null);
    setNotice(null);
  };

  const saveEdit = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    const value = Number(editThreshold);
    if (!Number.isFinite(value) || value < 0) { setActionError(t('m41.quality.thresholdNonNeg')); return; }
    try {
      await apiRequest(`/v1/data-lake/data-quality/${id}`, { method: 'PUT', body: { threshold: value, severity: editSeverity }, token });
      setNotice(t('m41.quality.ruleUpdated'));
      setEditingId(null);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.quality.notAuthUpdate'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const remove = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/data-lake/data-quality/${id}`, { method: 'DELETE', token });
      setNotice(t('m41.quality.ruleDeleted'));
      if (editingId === id) setEditingId(null);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.quality.notAuthDelete'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="data-quality-dashboard">
      <h1>{t('m41.quality.title')}</h1>

      <section aria-label="quality-define">
        <h2>{t('m41.quality.defineRule')}</h2>
        <form aria-label="define-form" onSubmit={(e) => void define(e)}>
          <label>{t('m41.common.datasetId')}<input aria-label="field-dataset-id" value={datasetId} onChange={(e) => setDatasetId(e.target.value)} /></label>
          <label>{t('m41.quality.dimension')}
            <select aria-label="field-dimension" value={dimension} onChange={(e) => setDimension(e.target.value)}>
              {DIMENSIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label>{t('m41.quality.threshold')}<input aria-label="field-threshold" type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} /></label>
          <label>{t('m41.common.severity')}
            <select aria-label="field-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
              {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-define">{t('m41.common.define')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="quality-action-error">{actionError}</p>}
        {notice && <p aria-label="quality-notice">{notice}</p>}
      </section>

      <section aria-label="quality-filters">
        <label>{t('m41.common.datasetId')}<input aria-label="filter-dataset-id" value={datasetFilter} onChange={(e) => { setDatasetFilter(e.target.value); setPage(0); }} /></label>
        <label>{t('m41.quality.dimension')}
          <select aria-label="filter-dimension" value={dimensionFilter} onChange={(e) => { setDimensionFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m41.common.all')}</option>
            {DIMENSIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label>{t('m41.quality.result')}
          <select aria-label="filter-result" value={resultFilter} onChange={(e) => { setResultFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m41.common.all')}</option>
            {RESULTS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert" aria-label="quality-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="quality-total">{t('m41.quality.total', { count: total })}</p>
          <DataTable
            ariaLabel="quality-table"
            rows={rows}
            rowKey={(q) => q.qualityId}
            emptyMessage={t('m41.quality.empty')}
            columns={[
              { key: 'code', header: t('m41.common.code'), cellAriaLabel: (q) => `quality-code-${q.qualityId}`, render: (q) => q.qualityCode },
              { key: 'dimension', header: t('m41.quality.dimension'), render: (q) => q.dimension },
              { key: 'threshold', header: t('m41.quality.threshold'), render: (q) => q.threshold ?? '—' },
              { key: 'score', header: t('m41.quality.score'), cellAriaLabel: (q) => `quality-score-${q.qualityId}`, render: (q) => decimal(q.score) },
              { key: 'result', header: t('m41.quality.result'), cellAriaLabel: (q) => `quality-result-${q.qualityId}`, render: (q) => q.resultStatus },
              { key: 'severity', header: t('m41.common.severity'), render: (q) => q.severity },
              { key: 'evaluate', header: t('m41.quality.evaluate'), render: (q) => (
                <>
                  <input aria-label={`checked-${q.qualityId}`} type="number" placeholder={t('m41.quality.checkedPh')}
                    value={runChecked[q.qualityId] ?? ''} onChange={(e) => setRunChecked((m) => ({ ...m, [q.qualityId]: e.target.value }))} />
                  <input aria-label={`passed-${q.qualityId}`} type="number" placeholder={t('m41.quality.passedPh')}
                    value={runPassed[q.qualityId] ?? ''} onChange={(e) => setRunPassed((m) => ({ ...m, [q.qualityId]: e.target.value }))} />
                  <Button variant="secondary" type="button" aria-label={`run-${q.qualityId}`} onClick={() => void run(q.qualityId)}>{t('m41.common.run')}</Button>
                </>
              ) },
              { key: 'actions', header: t('m41.common.actions'), render: (q) => (
                editingId === q.qualityId ? (
                  <>
                    <input aria-label={`edit-threshold-${q.qualityId}`} type="number" min="0" value={editThreshold} onChange={(e) => setEditThreshold(e.target.value)} />
                    <select aria-label={`edit-severity-${q.qualityId}`} value={editSeverity} onChange={(e) => setEditSeverity(e.target.value)}>
                      {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <Button variant="primary" type="button" aria-label={`save-${q.qualityId}`} onClick={() => void saveEdit(q.qualityId)}>{t('common.save')}</Button>
                    <Button variant="danger" type="button" aria-label={`cancel-${q.qualityId}`} onClick={() => setEditingId(null)}>{t('common.cancel')}</Button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" type="button" aria-label={`edit-${q.qualityId}`} onClick={() => startEdit(q)}>{t('m41.common.edit')}</Button>
                    <Button variant="danger" type="button" aria-label={`delete-${q.qualityId}`} onClick={() => void remove(q.qualityId)}>{t('common.delete')}</Button>
                  </>
                )
              ) },
            ]}
          />

          <nav aria-label="quality-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m41.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m41.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m41.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
